import { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Avatar, Box, IconButton, Toolbar, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { selectAuthUser } from "../../store/slices/authSlice";
import UserMenu from "./UserMenu";

const ringSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const AnimatedIconButton = styled(IconButton)(() => ({
  position: "relative",
  borderRadius: "50%",
  width: 48,
  height: 48,
  padding: 0,
  color: "#ffffff",
  backgroundColor: "#000000",
  overflow: "hidden",
  transition: "background-color 0.3s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    padding: 2,
    background: "conic-gradient(from 0deg, #0ea5e9, #00a76f, #0ea5e9)",
    animation: `${ringSpin} 2.8s linear infinite`,
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
  },
  "&:hover": {
    backgroundColor: "#3a3939ff",
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

const colorFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  60% { opacity: 1; transform: translateY(0); }
  100% { opacity: 1; transform: translateY(0); }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: "-0.01em",
  // backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, #e0f2fe)`,
  // backgroundImage: `linear-gradient(90deg, #00c2ff, #33ff8c, #e54cff, #e0f2fe)`,
  backgroundImage: `linear-gradient(90deg, #ffd194, #70e1f5, #FFEFBA, #e0f2fe)`,  
  backgroundSize: "300% auto",
  color: "transparent",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  animation: `${colorFlow} 6s linear infinite, ${fadeIn} 900ms ease forwards`,
  display: "inline-block",
  position: "relative",
  opacity: 0,
  textShadow: "0 15px 30px rgba(0, 0, 0, 0.4)",
}));

const Header = ({ drawerWidth, onMenuClick }) => {
  const user = useSelector(selectAuthUser);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleOpenMenu = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const initials = (user?.name || "Admin")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="inherit"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        // backgroundImage: (theme) =>
        //   `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, rgba(2,6,23,0.92) 50%, ${theme.palette.primary.main} 100%)`,
        // backgroundImage: (theme) =>
        //   `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, #000046 50%, #1CB5E0 100%)`,
        // backgroundImage: (theme) =>
        //   `linear-gradient(120deg, #2C3E50 0%, #FD746C 50%, #2C3E50 100%)`,
        // backgroundImage: (theme) =>
        //   `linear-gradient(120deg, #4CA1AF 0%, #C4E0E5 50%, #4CA1AF 100%)`,
        backgroundImage: (theme) =>
          `linear-gradient(120deg, #292E49 0%, #536976 50%, #BBD2C5 100%)`,        
        // backgroundImage: (theme) =>
        //   `linear-gradient(120deg, #0052D4 0%, #65C7F7 50%, #9CECFB 100%)`,                
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: "1px solid rgba(148, 163, 184, 0.22)",
        "&::after": {
          content: '""',
          position: "absolute",
          left: 16,
          right: 16,
          bottom: -1,
          height: 2,
          borderRadius: 999,
          background:
            "linear-gradient(90deg, rgba(34,211,238,0), rgba(34,211,238,0.45) 40%, rgba(16,185,129,0.45) 60%, rgba(16,185,129,0))",
          boxShadow: "0 0 18px rgba(34, 211, 238, 0.25)",
          pointerEvents: "none",
          zIndex: 0,
        },
        borderRadius: 0,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: { xs: 2, sm: 4 },
          minHeight: 72,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{
              mr: 1,
              display: { md: "none" },
              color: "#ffffff",
            }}
          >
            <MenuIcon />
          </IconButton>
          <AnimatedTitle variant="h6" sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, whiteSpace: "nowrap" }}>
            Special Lane CMS
          </AnimatedTitle>
        </Box>
        <AnimatedIconButton onClick={handleOpenMenu}>
          <Avatar
            sx={{
              bgcolor: "transparent",
              color: "#ffffff",
              width: 40,
              height: 40,
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        </AnimatedIconButton>
        <UserMenu anchorEl={menuAnchor} onClose={handleCloseMenu} />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
